import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import CollapsibleDiv from 'react-collapse';
import isEqual from 'react-fast-compare';
import classNames from 'classnames';
import Blurb from '~shared/ui/Blurb';
import { alterMarketName } from '~shared/utils/market-utils';
import {
    useMarketName,
    useMarketSelectionSchema,
    useUiMarketName,
    useUiMarketSlug,
    useIsMarketCashoutRestricted,
} from '~SpectateStore/hooks/marketsHooks';
import {
    useMarketHasSelections,
    useUiMarketHasSelections,
} from '~SpectateStore/hooks/selectionsHooks';
import { useSportSlugByEventId } from '~SpectateStore/hooks/sportsHooks';
import {
    useHomeTeamName,
    useAwayTeamName,
} from '~SpectateStore/hooks/eventsHooks';
import { useIsUsFormat } from '~SpectateStore/hooks/categoriesHooks';
import { useCashoutAvailable } from '~SpectateStore/hooks/eventsHooks';
import SelectionsGridScores from './SelectionsGridScores';
import SelectionsGridScoresUS from './SelectionsGridScoresUS';
import SelectionsGridColumns from './SelectionsGridColumns';
import SelectionsGridBasic from './SelectionsGridBasic';
import SelectionsGridGameLine from './SelectionsGridGameLine';
import SelectionsGridGoalscorer from './SelectionsGridGoalscorer';

const MarketCard = memo(({ eventId, marketId, expand, marketDescription }) => {
    const [ t ] = useTranslation();
    const isUsFormat = useIsUsFormat(eventId);
    const name = useMarketName(marketId);
    const homeTeamName = useHomeTeamName(eventId);
    const awayTeamName = useAwayTeamName(eventId);
    const sportSlug = useSportSlugByEventId(eventId);
    let uiMarketName = useUiMarketName(marketId);
    const uiMarketSlug = useUiMarketSlug(marketId);
    uiMarketName = (!uiMarketName && uiMarketSlug) ? t(`mkt_headers.${uiMarketSlug}`) : uiMarketName;
    const selectionSchema = useMarketSelectionSchema(marketId);
    const hasActiveSelections = useMarketHasSelections(eventId, marketId);
    const hasActiveUiMarketSelections = useUiMarketHasSelections(eventId, marketId);
    const isCashoutRestrictedForMarket = useIsMarketCashoutRestricted(marketId);
    const isCashoutAvailable = useCashoutAvailable(eventId);
    const [isExpanded, setIsExpanded] = useState(expand ?? true);

    const marketHeaderClass = classNames('market-inplay-header', {
        'expanded': isExpanded,
    });

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    useEffect(() => {
        setIsExpanded(expand);
    }, [expand]);

    const renderSelectionsGrid = () => {
        if (marketId.includes('gameLine')) {
            return <SelectionsGridGameLine
                eventId={eventId}
                marketId={marketId}
            />;
        }

        if (marketId.includes('group')) {
            return <SelectionsGridGoalscorer
                eventId={eventId}
                marketId={marketId}
            />;
        }

        // Pick the appropriate component to present the selections
        switch(Number(selectionSchema)) {
            case 6: {
                // US Correct Score market
                if (isUsFormat) {
                    return <SelectionsGridScoresUS
                        eventId={eventId}
                        marketId={marketId}
                    />;
                }

                // Correct Score market
                return <SelectionsGridScores
                    eventId={eventId}
                    marketId={marketId}
                />;
            }
            case 12: {
                // Columns format with Over/Under Headers
                const headers = [
                    t('mkt_headers.over'),
                    t('mkt_headers.under'),
                ];

                return <SelectionsGridColumns
                    eventId={eventId}
                    marketId={marketId}
                    headers={headers}
                />;
            }
            case 13:
                // Yes/No market
                return (
                    <SelectionsGridBasic
                        eventId={eventId}
                        marketId={marketId}
                        isYesNoMarket
                    />
                );
            case 70:
                // Swish market (Over/Under)
                return (
                    <SelectionsGridColumns
                        eventId={eventId}
                        marketId={marketId}
                        headers={[
                            t('mkt_headers.over'),
                            t('mkt_headers.under'),
                        ]}
                        showSelectionLabels
                    />
                );
            case 71:
                // Swish market (Yes/No)
                return (
                    <SelectionsGridColumns
                        eventId={eventId}
                        marketId={marketId}
                        headers={[
                            t('mkt_headers.yes'),
                            t('mkt_headers.no'),
                        ]}
                    />
                );
            default:
                return (
                    <SelectionsGridBasic
                        eventId={eventId}
                        marketId={marketId}
                    />
                );
        }
    };

    if (!hasActiveUiMarketSelections && !hasActiveSelections) {
        return null;
    }

    const newMarketName = alterMarketName(name || uiMarketName, homeTeamName, awayTeamName, sportSlug);

    return (
        <div
            key={`market_inplay_id_${marketId}`}
            data-market-schema={selectionSchema}
            className="market-card"
        >
            <h4
                onClick={toggleExpanded}
                className={marketHeaderClass}
            >
                <span dir="auto">{newMarketName}</span>
                {
                    isCashoutAvailable && !isCashoutRestrictedForMarket
                    ? <span className="cashout-available-icon"></span>
                    : null
                }
            </h4>
            <CollapsibleDiv
                isOpened={isExpanded}
                className="selectionContent"
            >
                {
                    marketDescription
                        ? <Blurb text={marketDescription} />
                        : null
                }
                {renderSelectionsGrid()}
            </CollapsibleDiv>
        </div>
    );
}, isEqual);

MarketCard.displayName = 'MarketCard';

MarketCard.propTypes = {
    eventId: PropTypes.oneOfType([
        PropTypes.string.isRequired,
        PropTypes.number.isRequired,
    ]),
    marketId: PropTypes.oneOfType([
        PropTypes.string.isRequired,
        PropTypes.number.isRequired,
    ]),
    expand: PropTypes.bool,
    marketDescription: PropTypes.string,
};

export default MarketCard;
